import React from 'react';
import { RecommendationResponse } from '../services/recommendationService';
import RecommendationCard from './RecommendationCard';

interface RecommendationResultsProps {
  results: RecommendationResponse | null;
  loading?: boolean;
  error?: string | null;
  onBack?: () => void;
  className?: string;
}

const RecommendationResults: React.FC<RecommendationResultsProps> = ({
  results,
  loading = false,
  error = null,
  onBack,
  className = ""
}) => {
  if (loading) {
    return (
      <div className={`flex items-center justify-center py-12 ${className}`}>
        <div className="text-center">
          <svg className="animate-spin mx-auto h-12 w-12 text-green-ink mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Analyzing Equipment Database
          </h3>
          <p className="text-gray-600">
            Finding the best equipment matches for your criteria...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`py-12 ${className}`}>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error Getting Recommendations
              </h3>
              <p className="mt-1 text-sm text-red-700">
                {error}
              </p>
              {onBack && (
                <button
                  onClick={onBack}
                  className="mt-3 text-sm text-red-800 underline hover:text-red-600"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className={`py-12 ${className}`}>
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-1">No recommendations yet</h3>
          <p className="text-sm text-gray-500">Fill out the form above and hit "Get Quick Recommendations" to see results.</p>
        </div>
      </div>
    );
  }

  const { recommendations, equipment_type, criteria_summary, related_questions, confidence_level } = results;

  // Parse criteria summary into chips
  const criteriaChips = criteria_summary && criteria_summary !== 'general search'
    ? criteria_summary.split(', ').filter(Boolean)
    : [];

  // Get unique manufacturers, capped at 5
  const uniqueManufacturers = recommendations
    .map(r => r.manufacturer)
    .filter((v, i, a) => a.indexOf(v) === i);
  const displayedManufacturers = uniqueManufacturers.slice(0, 5);
  const extraCount = uniqueManufacturers.length - displayedManufacturers.length;

  return (
    <div className={`space-y-6 ${className}`}>

      {/* Results Header */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
        <div className="flex items-start sm:items-center justify-between gap-3 mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 capitalize">
            Top {equipment_type} for You
          </h2>
          <span className={`flex-shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            confidence_level === 'high' ? 'bg-green/15 text-green-ink' :
            confidence_level === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
          }`}>
            {confidence_level === 'high' ? 'High' : confidence_level === 'medium' ? 'Medium' : 'Low'} confidence
          </span>
        </div>

        <p className="text-gray-600 mb-4">
          We found <span className="font-semibold">{recommendations.length} {equipment_type}</span> that match your criteria.
          Click on any card to see detailed scoring.
        </p>

        <div className="text-sm text-gray-500">
          Showing {equipment_type} from {displayedManufacturers.join(', ')}
          {extraCount > 0 && ` and ${extraCount} more`}
        </div>
      </div>

      {/* Criteria Summary as Chips */}
      {criteriaChips.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3">
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-xs font-semibold text-blue-800 uppercase tracking-wide mr-1">Your criteria:</span>
            {criteriaChips.map((chip, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
              >
                {chip}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Recommendation Cards */}
      <div className="space-y-4">
        {recommendations.map((recommendation, index) => (
          <React.Fragment key={`${recommendation.equipment_id}-${index}`}>
            {index === 3 && recommendations.length > 3 && (
              <div className="flex items-center gap-3 py-1">
                <div className="flex-1 border-t border-gray-200"></div>
                <span className="text-xs text-gray-400 uppercase tracking-wide whitespace-nowrap">Other Good Options</span>
                <div className="flex-1 border-t border-gray-200"></div>
              </div>
            )}
            <RecommendationCard
              recommendation={recommendation}
              className="hover:ring-2 hover:ring-green-ink transition-all"
            />
          </React.Fragment>
        ))}
      </div>

      {/* Related Questions */}
      {related_questions && related_questions.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Related Questions
          </h3>
          <div className="space-y-3">
            {related_questions.map((question, index) => (
              <button
                key={index}
                className="w-full text-left p-3 bg-green/10 hover:bg-green/15 rounded-lg transition-colors group"
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3">
                    <div className="w-6 h-6 bg-green-ink rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">?</span>
                    </div>
                  </div>
                  <span className="text-green-ink group-hover:text-green-ink">
                    {question}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sources Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Sources
        </h3>
        <div className="space-y-2">
          {[
            `${equipment_type.charAt(0).toUpperCase() + equipment_type.slice(1)} Product Catalog 2023`,
            'Golf Equipment Technical Specifications Guide',
            'Performance Test Data',
            'PGA Tour Performance Data',
            'Golf Digest Equipment Review',
            'Official manufacturer websites'
          ].map((source, index) => (
            <div key={index} className="flex items-center text-sm text-gray-600">
              <svg className="w-4 h-4 text-green-ink mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
              </svg>
              {source}
            </div>
          ))}
        </div>
      </div>

      {/* Back to Query Form */}
      {onBack && (
        <div className="flex justify-center pt-6">
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Query Form
          </button>
        </div>
      )}
    </div>
  );
};

export default RecommendationResults;